/**
 * User Activity Tracking Service
 *
 * Tracks genuine user activity (mouse, keyboard, clicks, touch, scroll)
 * to determine if the user is actively using the application.
 */

class UserActivityService {
  constructor() {
    // Configuration
    this.idleThresholdMs = 10 * 60 * 1000; // 10 minutes (matches Modal scaledown)
    this.debounceMs = 1000; // Debounce activity events by 1 second

    // State
    this.lastActivityTime = 0;
    this.isInitialized = false;
    this.debounceTimer = null;

    // Callbacks
    this.onActivityCallbacks = [];

    // Bound event handler (for cleanup)
    this._boundActivityHandler = this._onActivity.bind(this);
    this._boundVisibilityHandler = this._onVisibilityChange.bind(this);
  }

  /**
   * Initialize activity tracking
   * Should be called once when user is authenticated
   */
  init() {
    if (this.isInitialized) return;

    // Track these events as indicators of genuine activity
    var events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click"
    ];

    var self = this;
    events.forEach(function (event) {
      window.addEventListener(event, self._boundActivityHandler, {
        passive: true
      });
    });

    // Also track visibility changes
    document.addEventListener("visibilitychange", this._boundVisibilityHandler);

    // Set initial activity time
    this.lastActivityTime = Date.now();
    this.isInitialized = true;

    console.log("[UserActivityService] Initialized");
  }

  /**
   * Clean up event listeners
   * Should be called on logout or app destruction
   */
  destroy() {
    if (!this.isInitialized) return;

    var events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click"
    ];
    var self = this;
    events.forEach(function (event) {
      window.removeEventListener(event, self._boundActivityHandler);
    });

    document.removeEventListener(
      "visibilitychange",
      this._boundVisibilityHandler
    );

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.onActivityCallbacks = [];
    this.isInitialized = false;
    console.log("[UserActivityService] Destroyed");
  }

  /**
   * Register callback for when user becomes active after being idle
   */
  onActivity(callback) {
    this.onActivityCallbacks.push(callback);
  }

  /**
   * Check if user is currently active (had activity within threshold)
   */
  isActive() {
    return Date.now() - this.lastActivityTime < this.idleThresholdMs;
  }

  /**
   * Check if tab is visible
   */
  isTabVisible() {
    return document.visibilityState === "visible";
  }

  /**
   * Get time since last activity in milliseconds
   */
  getTimeSinceLastActivity() {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Internal: Handle activity events with debouncing
   */
  _onActivity() {
    var self = this;

    // Debounce to avoid excessive updates
    if (this.debounceTimer) return;

    this.debounceTimer = setTimeout(function () {
      self.debounceTimer = null;
    }, this.debounceMs);

    var wasIdle = !this.isActive();
    this.lastActivityTime = Date.now();

    // If user was idle and is now active, notify callbacks
    if (wasIdle) {
      console.log("[UserActivityService] User became active");
      this.onActivityCallbacks.forEach(function (cb) {
        try {
          cb();
        } catch (e) {
          console.error("[UserActivityService] Activity callback error:", e);
        }
      });
    }
  }

  /**
   * Internal: Handle visibility changes
   */
  _onVisibilityChange() {
    if (document.visibilityState === "visible") {
      // Tab became visible - treat as activity
      this._onActivity();
    }
  }
}

// Export singleton instance
export default new UserActivityService();
